import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@app/components/shared/Card";
import { Button } from "@app/components/shared/Button";
import { Skeleton } from "@app/components/shared/Skeleton";
import {
  getSettingsSchema,
  updateSettings,
  resetSettings,
  getAuditLog,
  exportSettings,
  previewImport,
  importSettings,
  type SettingsSchema,
  type SettingDefinition,
  type SettingUpdate,
  type AuditLogEntry,
  type ImportRequest,
  type ImportPreviewResponse,
  type ImportPreviewDiff,
} from "@app/services/adminSettingsApi";
import {
  getExecutionStatus,
  controlExecution,
} from "@app/services/tradingSystemApi";
import {
  getTradingKillSwitches,
  updateExitFallbackEnabled,
  type TradingKillSwitches,
} from "@app/services/adminSystemSettingsApi";
import {
  fetchNewsServicesStatus,
  toggleNewsService,
  type NewsServicesStatus,
  type NewsServiceToggleRequest,
} from "@app/services/marketDataApi";
import { useAppConfig } from "@app/hooks/useAppConfig";
import { logger } from "@app/services/logger";
import { SettingTooltip } from "./components/SettingTooltip";
import { LearningReportPanel } from "./components/LearningReportPanel";
import { AiIntelligencePanel } from "./components/AiIntelligencePanel";
import { AutoTuneTab } from "@app/features/auto-tune/AutoTuneTab";
import { AiEngineSettingsSection } from "./AiEngineSettingsSection";
import {
  updateShadowMode,
  getShadowStats,
  getShadowReadiness,
  type ShadowStats,
  type ShadowReadiness,
} from "@app/services/aiEngineApi";
import "./tradingLogicControls.css";

// Flow duplicate tracking map: setting_key -> flow tab number
// See plans/Trade Flow Tracking Admin Section.md for full reference
const FLOW_DUPLICATE_MAP: Record<string, number> = {
  // Tab 2: Genesis Filters
  "trading.minimum_coin_price": 2,
  "trading.enable_htf_trend_filter": 2,
  "trading.htf_trend_ema_period": 2,
  // Tab 3: Pipeline Filters
  "trading.exclude_flat_structure": 3,
  "trading.flat_structure_min_change_pct": 3,
  "trading.enable_direction_filter": 3,
  "trading.direction_min_change_1h": 3,
  "trading.enable_peak_avoidance_filter": 3,
  "trading.peak_avoidance_max_change_1h": 3,
  "trading.enable_movement_quality_filter": 3,
  "trading.movement_quality_vol_spread_mult": 3,
  "trading.enable_not_stable_filter": 3,
  "trading.enable_volume_support_filter": 3,
  "trading.enable_smart_direction_mode": 3,
  "trading.sideways_trap_enabled": 4,
  "genius_filter.enabled": 3,
  "genius_filter.r1_enabled": 3,
  "genius_filter.r1_gap_enabled": 3,
  "genius_filter.macro_bear_penalty": 3,
  "genius_filter.macro_strong_bear_penalty": 3,
  "genius_filter.nota_threshold": 3,
  "gate.xgboost_enabled": 3,
  "gate.xgboost_threshold": 3,
  "gate.xgboost_mode": 3,
  "gate.xgboost_prefilter_liquidity": 3,
  "gate.xgboost_prefilter_spread": 3,
  "gate.xgboost_prefilter_min_volatility": 3,
  "gate.xgboost_prefilter_flat_structure": 3,
  "gate.xgboost_prefilter_price_compression": 3,
  "gate.xgboost_prefilter_direction": 3,
  "gate.xgboost_prefilter_peak_avoidance": 3,
  "gate.xgboost_prefilter_movement_quality": 3,
  "gate.xgboost_prefilter_not_stable": 3,
  "gate.xgboost_prefilter_volume_support": 3,
  "gate.xgboost_prefilter_structure_position": 3,
  "gate.xgboost_prefilter_v4_enabled": 3,
  "gate.xgboost_prefilter_v4_uptrend": 3,
  "gate.xgboost_prefilter_v4_rsi_momentum": 3,
  "gate.xgboost_prefilter_v4_non_decay": 3,
  "gate.xgboost_prefilter_v4_volatility": 3,
  "gate.xgboost_prefilter_v6_enabled": 3,
  "gate.xgboost_prefilter_v6_piercing_rsi_max": 3,
  "gate.xgboost_prefilter_v6_tws_rsi_max": 3,
  "gate.xgboost_prefilter_v6_fib618_enabled": 3,
  "gate.xgboost_prefilter_v6_atr_threshold": 3,
  "gate.xgboost_prefilter_v6_atr_lower_bound": 3,
  "gate.xgboost_prefilter_v6_volatility_cap_enabled": 3,
  "gate.xgboost_prefilter_v6_pullback_enabled": 3,
  "gate.xgboost_prefilter_v6_entry_position_enabled": 3,
  "gate.xgboost_prefilter_v6_entry_position_max": 3,
  "gate.xgboost_prefilter_v7_enabled": 3,
  "gate.xgboost_prefilter_v7_rsi_min": 3,
  "gate.xgboost_prefilter_v7_trend_enabled": 3,
  "gate.xgboost_prefilter_v7_dist_ema50_enabled": 3,
  "gate.xgboost_prefilter_v7_dist_ema50_min": 3,
  "scoring.technical_weight": 4,
  "scoring.volatility_weight": 4,
  "scoring.liquidity_weight": 4,
  "scoring.sentiment_weight": 4,
  "scoring.structure_weight": 4,
  "scoring.top_context_weight": 4,
  "scoring.top_timing_weight": 4,
  "scoring.top_quality_weight": 4,
  "scoring.rsi_sweet_spot_min": 4,
  "scoring.rsi_sweet_spot_max": 4,
  "analysis.overall_score.trend_weight": 4,
  "analysis.overall_score.momentum_weight": 4,
  "analysis.overall_score.volatility_weight": 4,
  "analysis.overall_score.volume_weight": 4,
  "analysis.signal_strength.overall_factor": 4,
  "analysis.signal_strength.trend_factor": 4,
  "analysis.signal_strength.momentum_factor": 4,
  "analysis.signal_strength.pattern_factor": 4,
  "analysis.signal_strength.breakout_factor": 4,
  "analysis.signal_strength.ensemble_factor": 4,
  "composite.rr_gate_min": 4,
  "composite.min_direction_pillar": 4,
  "composite.min_timing_pillar": 4,
  "composite.min_quality_pillar": 4,
  "composite.chop_block_enabled": 4,
  "vwap.enabled": 3,
  "session_filter.enabled": 3,
  "regime.enabled": 3,
  "regime.allow_trending": 3,
  "regime.allow_ranging": 3,
  "regime.allow_volatile": 3,
  "regime.allow_chop": 3,
  "mtf.brain_signal_profile": 3,
  // Tab 5: Smart Pool
  "pool.min_hybrid_score": 5,
  "pool.micro_score_weight": 5,
  "pool.rr_bonus_max": 5,
  "queue.score_weight.setup_quality": 5,
  "queue.score_weight.momentum_readiness": 5,
  "queue.score_weight.anti_chop": 5,
  "queue.score_weight.time_to_target": 5,
  "queue.score_weight.execution_freshness": 5,
  "queue.score_weight.volume_context": 5,
  "queue.score_weight.tp_headroom": 5,
  // Tab 6: Active/Reserve
  "queue.refresh_interval_seconds": 6,
  "queue.max_age_minutes": 6,
  "queue.invalidate_on_trend_misalignment": 6,
  "gate.active_pool_min_score": 6,
  "gate.smart_pool_min_confidence": 6,
  "ranking.confidence_weight": 6,
  "ranking.volume_weight": 6,
  "ranking.trend_alignment_weight": 6,
  "ranking.tp_headroom_weight": 6,
  "mtf.refresh_profile": 6,
  // Tab 7: Live Gate
  "gate.enabled": 7,
  "gate.failure_window_seconds": 7,
  "gate.cooldown_seconds": 7,
  "gate.failure_threshold": 7,
  "gate.score_threshold_enabled": 7,
  "gate.timing_score_min": 7,
  "gate.context_score_min": 7,
  "gate.min_tp_headroom_percent": 7,
  "gate.filter.soft_vol_break.enabled": 7,
  "gate.filter.bearish_close.enabled": 7,
  "gate.filter.anti_peak_fomo.enabled": 7,
  "gate.filter.soft_delay.enabled": 7,
  "gate.filter.near_resistance.enabled": 7,
  "gate.filter.vwap_alignment.enabled": 7,
  "gate.filter.volume_context.enabled": 7,
  "gate.filter.mid_range.enabled": 7,
  "gate.filter.weak_breakout.enabled": 7,
  "gate.counter_signal_detection": 7,
  "gate.signal_direction_check": 7,
  "gate.soft_vol_break_mode": 7,
  "gate.soft_vol_multiplier": 7,
  "gate.anti_peak_distance_pct": 7,
  "gate.stale_drift_threshold": 7,
  "gate.defer.snooze_ttl_seconds": 7,
  "gate.defer.max_count": 7,
  "gate.defer.max_wait_seconds": 7,
  "volume_context.enabled": 7,
  "volume_context.min_volume_spike_ratio": 7,
  "volume_context.obv_confirmation_required": 7,
  "volume_context.cmf_threshold": 7,
  // Tab 8: Technical Gate
  "gate.technical_required_count": 8,
  "gate.rsi_enabled": 8,
  "gate.macd_enabled": 8,
  "gate.ema_enabled": 8,
  "gate.bollinger_enabled": 8,
  "gate.adx_enabled": 8,
  "gate.rsi_buy_zone_low": 8,
  "gate.rsi_buy_zone_high": 8,
  "scoring.indicator.rsi.enabled": 8,
  "scoring.indicator.macd.enabled": 8,
  "scoring.indicator.ema_cross.enabled": 8,
  "scoring.indicator.bollinger.enabled": 8,
  "scoring.indicator.volume.enabled": 8,
  "scoring.indicator.atr.enabled": 8,
  "scoring.indicator.stochastic.enabled": 8,
  "scoring.indicator.adx.enabled": 8,
  "scoring.indicator.rsi.weight": 8,
  "scoring.indicator.macd.weight": 8,
  "scoring.indicator.ema_cross.weight": 8,
  "scoring.indicator.bollinger.weight": 8,
  "scoring.indicator.volume.weight": 8,
  "scoring.indicator.atr.weight": 8,
  "scoring.indicator.stochastic.weight": 8,
  "scoring.indicator.adx.weight": 8,
  "indicator.rsi.period": 8,
  "indicator.rsi.overbought": 8,
  "indicator.rsi.oversold": 8,
  "indicator.macd.fast_period": 8,
  "indicator.macd.slow_period": 8,
  "indicator.macd.signal_period": 8,
  "indicator.ema_cross.fast_period": 8,
  "indicator.ema_cross.slow_period": 8,
  "indicator.bollinger.period": 8,
  "indicator.bollinger.std_dev": 8,
  "indicator.bollinger.squeeze_threshold": 8,
  "indicator.volume.sma_period": 8,
  "mtf.live_gate_profile": 8,
  // Tab 9: Sniper
  "sniper.enabled": 9,
  "sniper.window_seconds": 9,
  "sniper.poll_interval_ms": 9,
  "sniper.min_score_safe": 9,
  "sniper.min_score_aggressive": 9,
  "sniper.max_adverse_move_pct": 9,
  "sniper.adaptive_window_enabled": 9,
  "sniper.adaptive_window_min_seconds": 9,
  "sniper.adaptive_window_max_seconds": 9,
};

/**
 * Returns a FLOW badge string for duplicated settings.
 * Format: "[FLOW-N]" where N is the tab number in Trade Flow Tracking.
 */
function getFlowBadge(settingKey: string): string | null {
  const tabNum = FLOW_DUPLICATE_MAP[settingKey];
  if (!tabNum) return null;
  return `[FLOW-${tabNum}]`;
}

// XGBoost directional gates v4..v24 live in subsections named
// "XGBoost Filter v<N> (...)" inside the LiveEntryGate section. Each one has a
// master-enable boolean whose key matches gate.xgboost_prefilter_v<N>_enabled.
const XGBOOST_FILTER_SUBSECTION_RE = /^XGBoost Filter v\d+/;
const XGBOOST_FILTER_MASTER_KEY_RE = /^gate\.xgboost_prefilter_v\d+_enabled$/;

function isXgboostFilterSubsection(subsection: string): boolean {
  return XGBOOST_FILTER_SUBSECTION_RE.test(subsection);
}

function readFilterEnabled(
  settings: SettingDefinition[],
  pendingChanges: Map<string, any>,
): boolean {
  const master = settings.find((s) => XGBOOST_FILTER_MASTER_KEY_RE.test(s.key));
  if (!master) return false;
  const value =
    pendingChanges.get(master.key) ?? master.current_value ?? master.default_value;
  return value === true || value === "true" || value === 1 || value === "1";
}

type SectionName = "TradingSettings" | "LiveEntryGate" | "SmartEntrySniper" | "SignalPipelineFilters" | "TPSLSettings" | "LearningSystem" | "FeatureFlags" | "AiScoring" | "AutoTune" | "AiEngineSettings" | "RankingWeights" | "TradingFilters";
type SubTabName = "settings" | "news-services" | "learning-report" | "ai-intelligence" | "auto-tune";

export function TradingLogicControlsTab(): JSX.Element {
  const queryClient = useQueryClient();
  const [selectedSection, setSelectedSection] = useState<SectionName | null>(null);
  const [selectedSubTab, setSelectedSubTab] = useState<SubTabName>("settings");
  const [showAuditView, setShowAuditView] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<Map<string, any>>(new Map());
  const [showResetConfirm, setShowResetConfirm] = useState<{ scope: string; section?: string } | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importPreview, setImportPreview] = useState<ImportPreviewResponse | null>(null);
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [autoSaveError, setAutoSaveError] = useState<string | null>(null);
  
  // Check if user is admin
  const { data: appConfig } = useAppConfig();
  const isAdmin = appConfig?.role === "admin";
  
  // Load kill switches (for EXIT_FALLBACK_ENABLED)
  const {
    data: killSwitches,
    isLoading: isLoadingKillSwitches,
    refetch: refetchKillSwitches,
  } = useQuery({
    queryKey: ["tradingKillSwitches"],
    queryFn: getTradingKillSwitches,
    enabled: isAdmin === true,
    staleTime: 30000,
  });

  // Update EXIT_FALLBACK_ENABLED mutation
  const updateExitFallbackMutation = useMutation({
    mutationFn: updateExitFallbackEnabled,
    onSuccess: (updatedSwitches) => {
      queryClient.setQueryData(["tradingKillSwitches"], updatedSwitches);
      logger.info("EXIT_FALLBACK_ENABLED updated successfully");
    },
    onError: (error) => {
      logger.error("Error updating EXIT_FALLBACK_ENABLED:", error);
      alert(`Failed to update Exit Fallback: ${error instanceof Error ? error.message : "Unknown error"}`);
    },
  });

  // News services status
  const {
    data: newsServicesStatus,
    isLoading: newsServicesLoading,
    refetch: refetchNewsServices,
  } = useQuery({
    queryKey: ["newsServicesStatus"],
    queryFn: fetchNewsServicesStatus,
    enabled: isAdmin === true,
    refetchInterval: 5000, // Refresh every 5 seconds
  });
  
  // Toggle news service mutation
  const toggleNewsServiceMutation = useMutation({
    mutationFn: toggleNewsService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["newsServicesStatus"] });
      refetchNewsServices();
      logger.info("News service toggled successfully");
    },
    onError: (error) => {
      logger.error("Error toggling news service:", error);
      alert(`Failed to toggle news service: ${error instanceof Error ? error.message : "Unknown error"}`);
    },
  });

  // Engine Mode state
  const [selectedEngineMode, setSelectedEngineMode] = useState<string>("rule_based");
  const [showEngineModeConfirm, setShowEngineModeConfirm] = useState(false);
  const [pendingEngineMode, setPendingEngineMode] = useState<string | null>(null);

  // Shadow Mode toggle state
  const [showShadowConfirm, setShowShadowConfirm] = useState(false);
  const [shadowToggleTarget, setShadowToggleTarget] = useState(false);

  // Shadow Mode mutation
  const shadowModeMutation = useMutation({
    mutationFn: (enabled: boolean) => updateShadowMode(enabled),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["adminSettingsSchema"] });
      setShowShadowConfirm(false);
      logger.info(
        `Shadow Mode ${data.shadow_mode_enabled ? "enabled" : "disabled"} successfully`,
      );
    },
    onError: (error) => {
      logger.error(
        "Error updating shadow mode:",
        error instanceof Error ? error : new Error(String(error)),
      );
      alert(
        `Failed to update shadow mode: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    },
  });

  // Shadow stats query
  const { data: shadowStats } = useQuery({
    queryKey: ["shadowStats"],
    queryFn: getShadowStats,
    refetchInterval: 30000,
  });

  // Shadow readiness query
  const { data: shadowReadiness } = useQuery({
    queryKey: ["shadowReadiness"],
    queryFn: getShadowReadiness,
    refetchInterval: 60000,
  });

  // Load settings schema
  const {
    data: schema,
    isLoading: schemaLoading,
    error: schemaError,
    refetch: refetchSchema,
  } = useQuery({
    queryKey: ["adminSettingsSchema"],
    queryFn: async () => {
      const data = await getSettingsSchema(undefined, true);
      // Debug: Log schema structure
      console.log("Schema received:", {
        type: Array.isArray(data) ? "array" : typeof data,
        isArray: Array.isArray(data),
        keys: Array.isArray(data) ? `Array length: ${data.length}` : Object.keys(data),
        firstKey: Array.isArray(data) ? data[0] : Object.keys(data)[0],
      });
      return data;
    },
    staleTime: 30000,
  });

  // Extract shadow mode current value from settings schema
  const shadowModeEnabled = useMemo(() => {
    if (!schema || Array.isArray(schema)) return false;
    const aiSection = (schema as Record<string, any>)["AiEngineSettings"];
    if (!aiSection || typeof aiSection !== "object") return false;
    for (const subsection of Object.values(aiSection) as any) {
      if (Array.isArray(subsection)) {
        const shadowSetting = subsection.find(
          (s: any) => s.key === "ai_engine.shadow_mode",
        );
        if (shadowSetting) {
          return Boolean(shadowSetting.current_value ?? false);
        }
      }
    }
    return false;
  }, [schema]);

  // Load execution status (simpler and more reliable than Circuit Breaker)
  const {
    data: executionStatusData,
    isLoading: execStatusLoading,
    refetch: refetchExecutionStatus,
  } = useQuery({
    queryKey: ["executionStatus"],
    queryFn: () => getExecutionStatus(),
    refetchInterval: 5000, // Refresh every 5 seconds
  });

  // Update settings mutation
  const updateMutation = useMutation({
    mutationFn: updateSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminSettingsSchema"] });
      setPendingChanges(new Map());
      setAutoSaveError(null);
      logger.info("Settings updated successfully");
    },
    onError: (error) => {
      const errMsg = error instanceof Error ? error.message : String(error);
      logger.error("Error updating settings:", error instanceof Error ? error : new Error(errMsg));
      setAutoSaveError(errMsg || "Failed to save settings. Try again.");
    },
  });

  // Reset settings mutation
  const resetMutation = useMutation({
    mutationFn: resetSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminSettingsSchema"] });
      setPendingChanges(new Map());
      setShowResetConfirm(null);
      logger.info("Settings reset successfully");
    },
    onError: (error) => {
      logger.error("Error resetting settings:", error);
    },
  });

  // Execution control mutations (using execution_status instead of Circuit Breaker)
  const pauseMutation = useMutation({
    mutationFn: () => controlExecution({ action: "pause" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["executionStatus"] });
      refetchExecutionStatus();
      logger.info("Execution paused successfully");
    },
    onError: (error) => {
      logger.error("Error pausing execution:", error);
      // Show user-friendly error message
      alert(`Failed to pause execution: ${error instanceof Error ? error.message : "Unknown error"}`);
    },
  });

  const resumeMutation = useMutation({
    mutationFn: async () => {
      logger.info("Resume mutation triggered - calling controlExecution");
      try {
        const result = await controlExecution({ action: "start" });
        logger.info("controlExecution completed successfully:", result);
        return result;
      } catch (error) {
        logger.error("Error in controlExecution:", error instanceof Error ? error : new Error(String(error)));
        throw error;
      }
    },
    onSuccess: (data) => {
      logger.info("Resume mutation onSuccess called:", data);
      queryClient.invalidateQueries({ queryKey: ["executionStatus"] });
      refetchExecutionStatus();
      logger.info("Execution resumed successfully");
    },
    onError: (error) => {
      logger.error("Error resuming execution:", error instanceof Error ? error : new Error(String(error)));
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      logger.error("Resume error details:", new Error(errorMessage), { errorMessage });
      alert(`Failed to resume execution: ${errorMessage}`);
    },
  });

  // Export mutation
  const exportMutation = useMutation({
    mutationFn: (tab?: string) => exportSettings(tab, false),
    onSuccess: (data) => {
      // Download JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `settings-export-${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      logger.info("Settings exported successfully");
    },
  });

  // Import mutations
  const previewImportMutation = useMutation({
    mutationFn: previewImport,
    onSuccess: (data) => {
      setImportPreview(data);
    },
  });

  const importMutation = useMutation({
    mutationFn: importSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminSettingsSchema"] });
      queryClient.invalidateQueries({ queryKey: ["auditLog"] });
      setShowImportModal(false);
      setImportPreview(null);
      logger.info("Settings imported successfully");
    },
  });

  // Handle setting change with auto-save
  const handleSettingChange = useCallback((key: string, value: any) => {
    // Clean tp_layers: allow 1 (single TP) or 2 (multi-TP) values; truncate if > 2.
    let cleanedValue = value;
    if (key === "trading.tp_layers" && Array.isArray(value)) {
      if (value.length > 2) {
        logger.info(
          `Cleaning tp_layers in Frontend: Found ${value.length} values, keeping only first 2: ${value.slice(0, 2)}`
        );
        cleanedValue = value.slice(0, 2);
      } else if (value.length === 0) {
        cleanedValue = [0.45, 0.6];
      }
    }
    
    // Clear any previous error when user makes a new change
    setAutoSaveError(null);

    setPendingChanges((prev) => {
      const next = new Map(prev);
      next.set(key, cleanedValue);
      return next;
    });

    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Set new timeout for auto-save (debounce 1 second)
    const timeout = setTimeout(async () => {
      const updates: SettingUpdate[] = [{ key, value: cleanedValue }];
      setIsSaving(true);
      setAutoSaveError(null);
      try {
        await updateMutation.mutateAsync({
          updates,
          scope: "admin",
        });
        setLastSavedAt(new Date());
        setAutoSaveError(null);
        setPendingChanges((prev) => {
          const next = new Map(prev);
          next.delete(key);
          return next;
        });
      } catch (error) {
        const errMsg = error instanceof Error ? error.message : String(error);
        logger.error("Auto-save failed:", error instanceof Error ? error : new Error(errMsg));
        setAutoSaveError(errMsg || "Auto-save failed. Check your connection and try again.");
      } finally {
        setIsSaving(false);
      }
    }, 1000);

    autoSaveTimeoutRef.current = timeout;
  }, [updateMutation]);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  // Handle save
  const handleSave = useCallback(async () => {
    if (pendingChanges.size === 0) {
      return;
    }

    const updates: SettingUpdate[] = Array.from(pendingChanges.entries()).map(([key, value]) => ({
      key,
      value,
    }));

    await updateMutation.mutateAsync({
      updates,
      scope: "admin",
    });
  }, [pendingChanges, updateMutation]);

  // Handle reset
  const handleReset = useCallback(async (scope: "all" | "section", section?: string) => {
    await resetMutation.mutateAsync({ scope, section });
  }, [resetMutation]);

  // Auto-select first section on load
  useEffect(() => {
    if (schema && !Array.isArray(schema) && typeof schema === "object") {
      const keys = Object.keys(schema);
      if (keys.length > 0 && !selectedSection) {
        const firstSection = keys[0] as SectionName;
        if (firstSection && typeof firstSection === "string") {
          setSelectedSection(firstSection);
        }
      }
    }
  }, [schema, selectedSection]);

  // Read engine mode from schema
  useEffect(() => {
    if (schema && !Array.isArray(schema) && typeof schema === "object") {
      const aiSection = schema["AiEngineSettings"];
      if (aiSection && typeof aiSection === "object") {
        for (const subsection of Object.values(aiSection) as any) {
          if (Array.isArray(subsection)) {
            const modeSetting = subsection.find(
              (s: any) => s.key === "recommendation.engine.mode"
            );
            if (modeSetting && modeSetting.current_value) {
              setSelectedEngineMode(String(modeSetting.current_value));
              break;
            }
          }
        }
      }
    }
  }, [schema]);

  // Engine Mode update mutation
  const engineModeMutation = useMutation({
    mutationFn: async (mode: string) => {
      await updateSettings({ updates: [{ key: "recommendation.engine.mode", value: mode }], scope: "admin" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminSettingsSchema"] });
      setShowEngineModeConfirm(false);
      setPendingEngineMode(null);
      logger.info("Engine mode updated successfully");
    },
    onError: (error) => {
      logger.error("Error updating engine mode:", error);
      alert(`Failed to update engine mode: ${error instanceof Error ? error.message : "Unknown error"}`);
    },
  });

  if (schemaLoading) {
    return (
      <div className="trading-logic-controls">
        <Skeleton height={400} />
      </div>
    );
  }

  if (schemaError) {
    return (
      <div className="trading-logic-controls">
        <Card>
          <h2>Error Loading Settings</h2>
          <p>{schemaError instanceof Error ? schemaError.message : "Unknown error"}</p>
          <Button onClick={() => refetchSchema()}>Retry</Button>
        </Card>
      </div>
    );
  }

  if (!schema) {
    return (
      <div className="trading-logic-controls">
        <Card>
          <h2>No Settings Available</h2>
        </Card>
      </div>
    );
  }

  return (
    <div className="trading-logic-controls">
      {/* Header */}
      <div className="trading-logic-controls__header">
        <div>
          <h1>Entry & Filter Settings</h1>
          {isSaving && (
            <p className="trading-logic-controls__save-status">
              Saving changes...
            </p>
          )}
          {lastSavedAt && !isSaving && !autoSaveError && (
            <p className="trading-logic-controls__save-status trading-logic-controls__save-status--saved">
              Changes saved automatically
            </p>
          )}
          {autoSaveError && !isSaving && (
            <p className="trading-logic-controls__save-status trading-logic-controls__save-status--error">
              {autoSaveError}
            </p>
          )}
        </div>
        <div className="trading-logic-controls__header-actions">
          <Button
            variant="secondary"
            onClick={() => setShowResetConfirm({ scope: "all" })}
            disabled={resetMutation.isPending}
          >
            Reset All
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowAuditView(!showAuditView)}
          >
            {showAuditView ? "Hide Audit" : "Show Audit"}
          </Button>
          <Button
            variant="secondary"
            onClick={() => exportMutation.mutate(selectedSection || undefined)}
            disabled={exportMutation.isPending}
          >
            {exportMutation.isPending ? "Exporting..." : "Export Settings"}
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowImportModal(true)}
          >
            Import Settings
          </Button>
        </div>
      </div>

      {/* Execution Controls Panel */}
      <ExecutionControlsPanel
        status={executionStatusData}
        isLoading={execStatusLoading}
        onPause={() => pauseMutation.mutate()}
        onResume={() => resumeMutation.mutate()}
        isPausing={pauseMutation.isPending}
        isResuming={resumeMutation.isPending}
      />

      {/* Engine Mode Selector (Admin only) */}
      {isAdmin && (
        <Card style={{ marginTop: "24px", marginBottom: "24px" }}>
          <div style={{ padding: "20px" }}>
            <h3 style={{
              fontSize: "1.1rem",
              fontWeight: 600,
              color: "rgba(191, 219, 254, 0.9)",
              marginBottom: "16px",
            }}>
              Engine Mode
            </h3>
            <div style={{
              display: "flex",
              gap: "16px",
              flexWrap: "wrap" as const,
            }}>
              {[
                {
                  value: "rule_based",
                  label: "Rule-Based",
                  description: "Current system — deterministic rules and thresholds",
                  color: "rgb(59, 130, 246)",
                  bgAlpha: "rgba(59, 130, 246, 0.15)",
                  textSelected: "rgb(147, 197, 253)",
                },
                {
                  value: "ai_technical",
                  label: "AI Technical",
                  description: "AI analyzes technical indicators — requires LLM connection",
                  color: "rgb(168, 85, 247)",
                  bgAlpha: "rgba(168, 85, 247, 0.15)",
                  textSelected: "rgb(196, 133, 250)",
                },
                {
                  value: "ai_orderbook",
                  label: "AI Orderbook",
                  description: "AI analyzes order book — requires LLM connection (future)",
                  color: "rgba(148, 163, 184, 0.6)",
                  bgAlpha: "rgba(148, 163, 184, 0.2)",
                  textSelected: "rgba(203, 213, 225, 0.9)",
                },
              ].map((mode) => (
                <button
                  key={mode.value}
                  onClick={() => {
                    if (mode.value !== selectedEngineMode) {
                      setPendingEngineMode(mode.value);
                      setShowEngineModeConfirm(true);
                    }
                  }}
                  disabled={engineModeMutation.isPending}
                  style={{
                    flex: "1 1 200px",
                    padding: "16px",
                    borderRadius: "10px",
                    border: selectedEngineMode === mode.value
                      ? `2px solid ${mode.color}`
                      : "2px solid rgba(148, 163, 184, 0.15)",
                    background: selectedEngineMode === mode.value
                      ? mode.bgAlpha
                      : "rgba(15, 23, 42, 0.6)",
                    cursor: engineModeMutation.isPending ? "not-allowed" : "pointer",
                    textAlign: "left" as const,
                    transition: "all 0.2s ease",
                    opacity: engineModeMutation.isPending ? 0.6 : 1,
                  }}
                >
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: "8px",
                  }}>
                    <div style={{
                      width: "18px",
                      height: "18px",
                      borderRadius: "50%",
                      border: selectedEngineMode === mode.value
                        ? `5px solid ${mode.color}`
                        : "2px solid rgba(148, 163, 184, 0.4)",
                      background: selectedEngineMode === mode.value
                        ? mode.color
                        : "transparent",
                      transition: "all 0.2s ease",
                      flexShrink: 0,
                    }} />
                    <span style={{
                      fontWeight: 600,
                      fontSize: "0.95rem",
                      color: selectedEngineMode === mode.value
                        ? mode.textSelected
                        : "rgba(203, 213, 225, 0.8)",
                    }}>
                      {mode.label}
                    </span>
                  </div>
                  <p style={{
                    fontSize: "0.8rem",
                    color: selectedEngineMode === mode.value
                      ? mode.textSelected
                      : "rgba(203, 213, 225, 0.6)",
                    lineHeight: 1.4,
                    margin: 0,
                    paddingLeft: "28px",
                  }}>
                    {mode.description}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Exit Fallback Kill Switch (Admin only) */}
      {isAdmin && (
        <Card style={{ marginTop: "24px", marginBottom: "24px" }}>
          <div style={{ padding: "20px" }}>
            <h3 style={{ 
              fontSize: "1.1rem", 
              fontWeight: 600, 
              color: "rgba(191, 219, 254, 0.9)",
              marginBottom: "16px",
            }}>
              Exit Fallback System
            </h3>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ flex: 1 }}>
                <label htmlFor="exit_fallback_enabled" style={{ 
                  display: "block",
                  fontSize: "0.95rem",
                  fontWeight: 500,
                  color: "rgba(203, 213, 225, 0.9)",
                  marginBottom: "8px",
                }}>
                  Enable Exit Fallback System
                </label>
                <p style={{ 
                  fontSize: "0.875rem", 
                  color: "rgba(203, 213, 225, 0.7)",
                  marginTop: "4px",
                }}>
                  Enable exit fallback state machine for handling stuck positions. When disabled, exit fallback service (retries, monitoring, cancels, sells) is completely stopped.
                </p>
              </div>
              <div style={{ marginLeft: "24px", display: "flex", alignItems: "center", gap: "12px" }}>
                {isLoadingKillSwitches ? (
                  <Skeleton width={100} height={24} />
                ) : (
                  <>
                    <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                      <input
                        id="exit_fallback_enabled"
                        type="checkbox"
                        checked={killSwitches?.EXIT_FALLBACK_ENABLED ?? false}
                        onChange={(e) => {
                          updateExitFallbackMutation.mutate(e.target.checked);
                        }}
                        disabled={updateExitFallbackMutation.isPending}
                        style={{ 
                          marginRight: "8px", 
                          cursor: updateExitFallbackMutation.isPending ? "not-allowed" : "pointer",
                          width: "18px",
                          height: "18px",
                        }}
                      />
                      <span style={{ 
                        fontSize: "0.875rem", 
                        color: "rgba(203, 213, 225, 0.7)",
                        fontWeight: 500,
                      }}>
                        {killSwitches?.EXIT_FALLBACK_ENABLED ? "Enabled" : "Disabled"}
                      </span>
                    </label>
                    {updateExitFallbackMutation.isPending && (
                      <span style={{ fontSize: "0.75rem", color: "rgba(203, 213, 225, 0.5)" }}>
                        Updating...
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* AI Engine Shadow Mode Card (Admin only) */}
      {isAdmin && (
        <Card style={{ marginTop: "24px", marginBottom: "24px" }}>
          <div style={{ padding: "20px" }}>
            <h3 style={{
              fontSize: "1.1rem",
              fontWeight: 600,
              color: "rgba(191, 219, 254, 0.9)",
              marginBottom: "16px",
            }}>
              AI Engine — Shadow Mode
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ flex: 1 }}>
                  <label htmlFor="shadow_mode_toggle" style={{
                    display: "block",
                    fontSize: "0.95rem",
                    fontWeight: 500,
                    color: "rgba(203, 213, 225, 0.9)",
                    marginBottom: "8px",
                  }}>
                    Shadow Mode
                  </label>
                  <p style={{
                    fontSize: "0.875rem",
                    color: "rgba(203, 213, 225, 0.7)",
                    marginTop: "4px",
                  }}>
                    When enabled, AI runs in parallel generating decisions for comparison.
                    AI decisions are logged but NOT added to the trading queue.
                    Rule-Based system continues as the primary queue feeder.
                  </p>
                </div>
                <div style={{ marginLeft: "24px" }}>
                  {shadowModeMutation.isPending ? (
                    <span style={{ fontSize: "0.75rem", color: "rgba(203, 213, 225, 0.5)" }}>
                      Updating...
                    </span>
                  ) : (
                    <button
                      id="shadow_mode_toggle"
                      onClick={() => {
                        const newTarget = !shadowModeEnabled;
                        setShadowToggleTarget(newTarget);
                        setShowShadowConfirm(true);
                      }}
                      style={{
                        padding: "8px 20px",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontWeight: 600,
                        fontSize: "0.9rem",
                        backgroundColor: shadowModeEnabled ? "rgba(34, 197, 94, 0.2)" : "rgba(239, 68, 68, 0.2)",
                        color: shadowModeEnabled ? "rgb(34, 197, 94)" : "rgb(239, 68, 68)",
                        border: shadowModeEnabled ? "1px solid rgba(34, 197, 94, 0.4)" : "1px solid rgba(239, 68, 68, 0.4)",
                      }}
                    >
                      {shadowModeEnabled ? "Enabled" : "Disabled"}
                    </button>
                  )}
                </div>
              </div>

              {/* Shadow Stats Row */}
              {shadowStats && shadowStats.total_analyzed > 0 && (
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: "12px",
                  marginTop: "8px",
                }}>
                  <div style={{
                    padding: "12px",
                    borderRadius: "8px",
                    background: "rgba(30, 41, 59, 0.5)",
                  }}>
                    <div style={{ fontSize: "0.75rem", color: "rgba(203, 213, 225, 0.6)", marginBottom: "4px" }}>
                      Analyzed
                    </div>
                    <div style={{ fontSize: "1.25rem", fontWeight: 700, color: "rgba(191, 219, 254, 0.9)" }}>
                      {shadowStats.total_analyzed}
                    </div>
                  </div>
                  <div style={{
                    padding: "12px",
                    borderRadius: "8px",
                    background: "rgba(30, 41, 59, 0.5)",
                  }}>
                    <div style={{ fontSize: "0.75rem", color: "rgba(203, 213, 225, 0.6)", marginBottom: "4px" }}>
                      Accepted
                    </div>
                    <div style={{ fontSize: "1.25rem", fontWeight: 700, color: "rgb(34, 197, 94)" }}>
                      {shadowStats.accepted}
                    </div>
                  </div>
                  <div style={{
                    padding: "12px",
                    borderRadius: "8px",
                    background: "rgba(30, 41, 59, 0.5)",
                  }}>
                    <div style={{ fontSize: "0.75rem", color: "rgba(203, 213, 225, 0.6)", marginBottom: "4px" }}>
                      Rejected
                    </div>
                    <div style={{ fontSize: "1.25rem", fontWeight: 700, color: "rgb(239, 68, 68)" }}>
                      {shadowStats.rejected}
                    </div>
                  </div>
                  <div style={{
                    padding: "12px",
                    borderRadius: "8px",
                    background: "rgba(30, 41, 59, 0.5)",
                  }}>
                    <div style={{ fontSize: "0.75rem", color: "rgba(203, 213, 225, 0.6)", marginBottom: "4px" }}>
                      Theoretical Win Rate
                    </div>
                    <div style={{ fontSize: "1.25rem", fontWeight: 700, color: "rgba(191, 219, 254, 0.9)" }}>
                      {shadowStats.theoretical_win_rate.toFixed(1)}%
                    </div>
                  </div>
                </div>
              )}

              {/* Readiness Progress Bars */}
              {shadowReadiness && (
                <div style={{ marginTop: "8px" }}>
                  <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "rgba(203, 213, 225, 0.8)", marginBottom: "12px" }}>
                    Shadow Exit Readiness
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    <ReadinessBar
                      label="Time (days)"
                      current={shadowReadiness.conditions.time.current_days}
                      required={shadowReadiness.conditions.time.required_days}
                      progress={shadowReadiness.conditions.time.progress_pct}
                      met={shadowReadiness.conditions.time.met}
                    />
                    <ReadinessBar
                      label="Trades Analyzed"
                      current={shadowReadiness.conditions.trades.current_trades}
                      required={shadowReadiness.conditions.trades.required_trades}
                      progress={shadowReadiness.conditions.trades.progress_pct}
                      met={shadowReadiness.conditions.trades.met}
                    />
                    <ReadinessBar
                      label="Win Rate"
                      current={shadowReadiness.conditions.win_rate.current_win_rate}
                      required={shadowReadiness.conditions.win_rate.required_win_rate}
                      progress={shadowReadiness.conditions.win_rate.progress_pct}
                      met={shadowReadiness.conditions.win_rate.met}
                    />
                  </div>
                  {shadowReadiness.ready && (
                    <div style={{
                      marginTop: "12px",
                      padding: "10px 16px",
                      borderRadius: "8px",
                      background: "rgba(34, 197, 94, 0.1)",
                      border: "1px solid rgba(34, 197, 94, 0.3)",
                      color: "rgb(34, 197, 94)",
                      fontWeight: 600,
                      fontSize: "0.9rem",
                      textAlign: "center",
                    }}>
                      All conditions met. Ready for active transition.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Engine Mode Confirmation Dialog */}
      {showEngineModeConfirm && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0, 0, 0, 0.6)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}>
          <div style={{
            background: "rgb(15, 23, 42)",
            borderRadius: "12px",
            padding: "32px",
            maxWidth: "520px",
            width: "90%",
            border: "1px solid rgba(148, 163, 184, 0.2)",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5)",
          }}>
            <h3 style={{
              fontSize: "1.15rem",
              fontWeight: 600,
              color: pendingEngineMode?.startsWith("ai_") ? "rgb(250, 204, 21)" : "rgb(59, 130, 246)",
              marginBottom: "16px",
            }}>
              {pendingEngineMode === "rule_based"
                ? "Switch to Rule-Based"
                : pendingEngineMode === "ai_technical"
                  ? "Switch to AI Technical"
                  : "Switch to AI Orderbook"}
            </h3>
            <p style={{
              fontSize: "0.9rem",
              color: "rgba(203, 213, 225, 0.8)",
              lineHeight: 1.6,
              marginBottom: "16px",
            }}>
              {pendingEngineMode === "rule_based" ? (
                "Switching to Rule-Based mode. AI will continue running in Shadow Mode for comparison. Queue cleanup will not occur."
              ) : pendingEngineMode === "ai_technical" ? (
                <>
                  <strong style={{ color: "rgb(250, 204, 21)" }}>Switching will queue cleanup.</strong>
                  {" "}AI mode requires active LLM connection (configured in Connections tab).
                  Current queue entries will be evaluated and may be removed. Continue?
                </>
              ) : (
                <>
                  <strong style={{ color: "rgb(250, 204, 21)" }}>AI Orderbook mode is a future feature.</strong>
                  {" "}The system will activate the engine framework but no decisions will be generated.
                  The Rule-Based system will remain as fallback. Continue?
                </>
              )}
            </p>
            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
              <button
                onClick={() => {
                  setShowEngineModeConfirm(false);
                  setPendingEngineMode(null);
                }}
                style={{
                  padding: "10px 24px",
                  borderRadius: "6px",
                  border: "1px solid rgba(148, 163, 184, 0.3)",
                  background: "transparent",
                  color: "rgba(203, 213, 225, 0.8)",
                  cursor: "pointer",
                  fontWeight: 500,
                  fontSize: "0.9rem",
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (pendingEngineMode) {
                    engineModeMutation.mutate(pendingEngineMode);
                  }
                }}
                disabled={engineModeMutation.isPending || !pendingEngineMode}
                style={{
                  padding: "10px 24px",
                  borderRadius: "6px",
                  border: "none",
                  background: engineModeMutation.isPending
                    ? "rgba(59, 130, 246, 0.4)"
                    : "rgb(59, 130, 246)",
                  color: "white",
                  cursor: engineModeMutation.isPending ? "not-allowed" : "pointer",
                  fontWeight: 600,
                  fontSize: "0.9rem",
                }}
              >
                {engineModeMutation.isPending ? "Switching..." : "Confirm Switch"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shadow Mode Confirmation Dialog */}
      {showShadowConfirm && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0, 0, 0, 0.6)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}>
          <div style={{
            background: "rgb(15, 23, 42)",
            borderRadius: "12px",
            padding: "32px",
            maxWidth: "520px",
            width: "90%",
            border: "1px solid rgba(148, 163, 184, 0.2)",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5)",
          }}>
            <h3 style={{
              fontSize: "1.15rem",
              fontWeight: 600,
              color: shadowToggleTarget ? "rgb(34, 197, 94)" : "rgb(239, 68, 68)",
              marginBottom: "16px",
            }}>
              {shadowToggleTarget ? "Confirm Activation" : "Confirm Deactivation"}
            </h3>
            <p style={{
              fontSize: "0.9rem",
              color: "rgba(203, 213, 225, 0.8)",
              lineHeight: 1.6,
              marginBottom: "24px",
            }}>
              {shadowToggleTarget
                ? "Enabling Shadow Mode will run AI in parallel for comparison. AI decisions will be logged but NOT added to the trading queue. Rule-Based system continues as the primary queue feeder."
                : "Disabling Shadow Mode will transfer active queue control to the AI Technical Engine. Current Rule-Based queue will be gradually drained. This action cannot be undone instantly."
              }
            </p>
            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
              <button
                onClick={() => setShowShadowConfirm(false)}
                style={{
                  padding: "10px 24px",
                  borderRadius: "6px",
                  border: "1px solid rgba(148, 163, 184, 0.3)",
                  background: "transparent",
                  color: "rgba(203, 213, 225, 0.8)",
                  cursor: "pointer",
                  fontWeight: 500,
                  fontSize: "0.9rem",
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => shadowModeMutation.mutate(shadowToggleTarget)}
                disabled={shadowModeMutation.isPending}
                style={{
                  padding: "10px 24px",
                  borderRadius: "6px",
                  border: "none",
                  background: shadowModeMutation.isPending
                    ? "rgba(239, 68, 68, 0.4)"
                    : "rgb(239, 68, 68)",
                  color: "white",
                  cursor: shadowModeMutation.isPending ? "not-allowed" : "pointer",
                  fontWeight: 600,
                  fontSize: "0.9rem",
                }}
              >
                {shadowModeMutation.isPending
                  ? "Switching..."
                  : shadowToggleTarget
                    ? "Confirm — Enable Shadow Mode"
                    : "Confirm — Switch to Live AI"
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sub-tabs Navigation (Admin only) */}
      {isAdmin && (
        <div className="trading-logic-controls__sub-tabs">
          <button
            className={`trading-logic-controls__sub-tab ${
              selectedSubTab === "settings" ? "active" : ""
            }`}
            onClick={() => setSelectedSubTab("settings")}
          >
            Settings
          </button>
          <button
            className={`trading-logic-controls__sub-tab ${
              selectedSubTab === "news-services" ? "active" : ""
            }`}
            onClick={() => setSelectedSubTab("news-services")}
          >
            News Services Control
          </button>
          <button
            className={`trading-logic-controls__sub-tab ${
              selectedSubTab === "ai-intelligence" ? "active" : ""
            }`}
            onClick={() => setSelectedSubTab("ai-intelligence")}
          >
            AI Intelligence
          </button>
        </div>
      )}

      {/* Main Content */}
      {showAuditView ? (
        <AuditView />
      ) : selectedSubTab === "news-services" && isAdmin ? (
        <NewsServicesControlPanel
          status={newsServicesStatus}
          isLoading={newsServicesLoading}
          onToggle={(service, enabled) => {
            toggleNewsServiceMutation.mutate({ service, enabled });
          }}
          isToggling={toggleNewsServiceMutation.isPending}
        />
      ) : selectedSubTab === "ai-intelligence" && isAdmin ? (
        <AiIntelligencePanel isVisible={true} />
      ) : (
        <div className="trading-logic-controls__content">
          {/* Section Navigation */}
          <div className="trading-logic-controls__sections-nav">
            {(() => {
              // Debug: Log schema structure
              if (schema) {
                console.log("Rendering schema:", {
                  isArray: Array.isArray(schema),
                  type: typeof schema,
                  keys: Array.isArray(schema) ? `Array[${schema.length}]` : Object.keys(schema),
                  firstFewKeys: Array.isArray(schema) 
                    ? schema.slice(0, 3).map((item: any, idx: number) => ({ index: idx, item }))
                    : Object.keys(schema).slice(0, 3),
                });
              }
              
              // Check if schema is valid object
              if (!schema) {
                return <div className="error-message"><p>No schema data available.</p></div>;
              }
              
              if (Array.isArray(schema)) {
                return (
                  <div className="error-message">
                    <p>Error: Schema is an array instead of an object!</p>
                    <p>Array length: {schema.length}</p>
                    <p>First item: {JSON.stringify(schema[0])}</p>
                  </div>
                );
              }
              
              if (typeof schema !== "object") {
                return (
                  <div className="error-message">
                    <p>Error: Schema is not an object! Type: {typeof schema}</p>
                  </div>
                );
              }
              
              const sectionKeys = Object.keys(schema).filter(
                (key) => typeof key === "string" && key.length > 0
              );
              
              if (sectionKeys.length === 0) {
                return (
                  <div className="error-message">
                    <p>No sections found in schema.</p>
                    <p>Schema keys: {JSON.stringify(Object.keys(schema))}</p>
                  </div>
                );
              }
              
              return sectionKeys.map((sectionName) => (
                <button
                  key={sectionName}
                  className={`trading-logic-controls__section-tab ${
                    selectedSection === sectionName ? "active" : ""
                  }`}
                  onClick={() => setSelectedSection(sectionName as SectionName)}
                >
                  {sectionName}
                </button>
              ));
            })()}
          </div>

          {/* Section Content */}
          {selectedSection === "LearningSystem" && selectedSubTab === "learning-report" ? (
            <LearningReportPanel isVisible={true} />
          ) : selectedSection === "AutoTune" ? (
            <AutoTuneTab />
          ) : selectedSection === "AiEngineSettings" && schema[selectedSection] ? (
            <AiEngineSettingsSection
              sectionName={selectedSection}
              sectionData={schema[selectedSection]}
              pendingChanges={pendingChanges}
              onSettingChange={handleSettingChange}
              onReset={() => setShowResetConfirm({ scope: "section", section: selectedSection })}
            />
          ) : selectedSection && schema[selectedSection] ? (
            <SettingsSection
              sectionName={selectedSection}
              sectionData={schema[selectedSection]}
              pendingChanges={pendingChanges}
              onSettingChange={handleSettingChange}
              onReset={() => setShowResetConfirm({ scope: "section", section: selectedSection })}
              onViewReport={selectedSection === "LearningSystem" ? () => setSelectedSubTab("learning-report") : undefined}
            />
          ) : (
            <Card>
              <p>Please select a section from the navigation menu.</p>
            </Card>
          )}
        </div>
      )}

      {/* Reset Confirmation Modal */}
      {showResetConfirm && (
        <ResetConfirmModal
          scope={showResetConfirm.scope}
          section={showResetConfirm.section}
          onConfirm={() => {
            handleReset(showResetConfirm.scope as "all" | "section", showResetConfirm.section);
          }}
          onCancel={() => setShowResetConfirm(null)}
          isResetting={resetMutation.isPending}
        />
      )}

      {/* Import Modal */}
      {showImportModal && (
        <ImportModal
          onClose={() => {
            setShowImportModal(false);
            setImportPreview(null);
          }}
          preview={importPreview}
          onPreview={(request) => previewImportMutation.mutate(request)}
          onImport={(request) => importMutation.mutate(request)}
          isPreviewing={previewImportMutation.isPending}
          isImporting={importMutation.isPending}
        />
      )}
    </div>
  );
}

// Settings Section Component
interface SettingsSectionProps {
  sectionName: string;
  sectionData: { [subsection: string]: SettingDefinition[] };
  pendingChanges: Map<string, any>;
  onSettingChange: (key: string, value: any) => void;
  onReset: () => void;
  onViewReport?: () => void;
}

function SettingsSection({
  sectionName,
  sectionData,
  pendingChanges,
  onSettingChange,
  onReset,
  onViewReport,
}: SettingsSectionProps) {
  const [showDisabledFilters, setShowDisabledFilters] = useState(false);

  // Partition subsections into inline (regular + enabled filters) and the
  // collapsed "disabled filters" list. A disabled filter is an XGBoost
  // directional gate (v4..v24) whose master-enable is currently off.
  const inlineSubsections: Array<[string, SettingDefinition[]]> = [];
  const disabledFilters: Array<[string, SettingDefinition[]]> = [];

  for (const [subsection, settings] of Object.entries(sectionData)) {
    const settingsArray = Array.isArray(settings) ? settings : [];
    if (settingsArray.length === 0) {
      continue;
    }

    if (
      isXgboostFilterSubsection(subsection) &&
      !readFilterEnabled(settingsArray, pendingChanges)
    ) {
      disabledFilters.push([subsection, settingsArray]);
    } else {
      inlineSubsections.push([subsection, settingsArray]);
    }
  }

  return (
    <div className="settings-section">
      <div className="settings-section__header">
        <h2>{sectionName}</h2>
        <div className="settings-section__header-actions">
          {onViewReport && (
            <Button variant="accent" onClick={onViewReport}>
              View Learning Report
            </Button>
          )}
          <Button variant="neutral" onClick={onReset}>
            Reset to Defaults
          </Button>
        </div>
      </div>

      {inlineSubsections.map(([subsection, settingsArray]) => (
        <SubsectionBlock
          key={subsection}
          subsection={subsection}
          settings={settingsArray}
          pendingChanges={pendingChanges}
          onSettingChange={onSettingChange}
        />
      ))}

      {disabledFilters.length > 0 && (
        <div className="disabled-filters-collapsible">
          <button
            type="button"
            className="disabled-filters-toggle"
            onClick={() => setShowDisabledFilters((prev) => !prev)}
            aria-expanded={showDisabledFilters}
          >
            <span>{showDisabledFilters ? "Hide" : "Show"} Disabled Filters</span>
            <span className="disabled-filters-toggle__count">
              {disabledFilters.length}
            </span>
          </button>
          {showDisabledFilters && (
            <div className="disabled-filters-content">
              {disabledFilters.map(([subsection, settingsArray]) => (
                <SubsectionBlock
                  key={subsection}
                  subsection={subsection}
                  settings={settingsArray}
                  pendingChanges={pendingChanges}
                  onSettingChange={onSettingChange}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface SubsectionBlockProps {
  subsection: string;
  settings: SettingDefinition[];
  pendingChanges: Map<string, any>;
  onSettingChange: (key: string, value: any) => void;
}

function SubsectionBlock({
  subsection,
  settings,
  pendingChanges,
  onSettingChange,
}: SubsectionBlockProps) {
  return (
    <div className="settings-subsection">
      {subsection !== "General" && <h3>{subsection}</h3>}
      <div className="settings-grid">
        {settings.map((setting, index) => (
          <SettingInput
            key={setting.key || `setting-${index}`}
            setting={setting}
            value={
              pendingChanges.get(setting.key) ??
              setting.current_value ??
              setting.default_value
            }
            onChange={(value) => onSettingChange(setting.key, value)}
          />
        ))}
      </div>
    </div>
  );
}

// Setting Input Component
interface SettingInputProps {
  setting: SettingDefinition;
  value: any;
  onChange: (value: any) => void;
}

function SettingInput({ setting, value, onChange }: SettingInputProps) {
  // For JSON textarea: local draft so user can type intermediate invalid JSON without revert
  const [jsonDraft, setJsonDraft] = useState<string | null>(null);
  const displayValue = setting.type === "json"
    ? (jsonDraft !== null ? jsonDraft : JSON.stringify(value ?? [], null, 2))
    : undefined;
  // Clear draft when parent value changes (e.g. after save/refetch)
  useEffect(() => {
    setJsonDraft(null);
  }, [value]);

  if (setting.is_readonly) {
    const flowBadge = getFlowBadge(setting.key);
    return (
      <div className="setting-input setting-input--readonly">
        <label>
          {setting.label}
          {flowBadge && (
            <span
              className="flow-duplicate-badge"
              title={`Also available in Trade Flow Tracking > Tab ${FLOW_DUPLICATE_MAP[setting.key]}`}
            >
              {flowBadge}
            </span>
          )}
        </label>
        <input type="text" value={value} readOnly />
        <span className="setting-input__readonly-badge">Read-only</span>
      </div>
    );
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    let newValue: any = e.target.value;
    
    if (setting.type === "bool") {
      newValue = (e.target as HTMLInputElement).checked;
    } else if (setting.type === "int") {
      newValue = parseInt(newValue, 10);
    } else if (setting.type === "float") {
      newValue = parseFloat(newValue);
    }

    onChange(newValue);
  };

  return (
    <div className={`setting-input setting-input--${setting.type}`}>
      <label>
        {setting.label}
        {(() => {
          const flowBadge = getFlowBadge(setting.key);
          if (!flowBadge) return null;
          return (
            <span
              className="flow-duplicate-badge"
              title={`Also available in Trade Flow Tracking > Tab ${FLOW_DUPLICATE_MAP[setting.key]}`}
            >
              {flowBadge}
            </span>
          );
        })()}
        <SettingTooltip
          description={setting.description}
          defaultValue={setting.default_value}
          constraints={setting.constraints}
          ariaLabel={`More information about ${setting.label}`}
        />
        {setting.is_dangerous && (
          <span className="setting-input__danger-badge">⚠️</span>
        )}
      </label>
      {setting.is_dangerous && setting.warning_text && (
        <p className="setting-input__warning">{setting.warning_text}</p>
      )}

      {setting.type === "bool" ? (
        <input
          type="checkbox"
          checked={value ?? false}
          onChange={handleChange}
        />
      ) : setting.type === "select" && setting.constraints?.options ? (
        <select value={value ?? ""} onChange={handleChange}>
          {(setting.constraints.options as (string | number | { value: string | number; label: string })[]).map((option) => {
            const optValue = typeof option === "object" ? option.value : option;
            const optLabel = typeof option === "object" ? option.label : option;
            return (
              <option key={optValue} value={optValue}>
                {optLabel}
              </option>
            );
          })}
        </select>
      ) : setting.type === "json" ? (
        <textarea
          value={displayValue}
          onChange={(e) => {
            const raw = e.target.value;
            setJsonDraft(raw);
            try {
              const parsed = JSON.parse(raw);
              // Clean tp_layers: allow 1 or 2 values; truncate if > 2.
              if (setting.key === "trading.tp_layers" && Array.isArray(parsed)) {
                if (parsed.length > 2) {
                  logger.info(
                    `Cleaning tp_layers in JSON input: Found ${parsed.length} values, keeping only first 2: ${parsed.slice(0, 2)}`
                  );
                  onChange(parsed.slice(0, 2));
                  setJsonDraft(null);
                  return;
                }
                if (parsed.length === 0) {
                  onChange([0.45, 0.6]);
                  setJsonDraft(null);
                  return;
                }
              }
              onChange(parsed);
              setJsonDraft(null);
            } catch {
              // Invalid JSON - keep draft so user can continue typing
            }
          }}
          onBlur={() => {
            // On blur, clear draft to show committed value (user may have left invalid JSON)
            setJsonDraft(null);
          }}
          rows={4}
        />
      ) : (
        <input
          type={setting.type === "float" || setting.type === "int" ? "number" : "text"}
          value={value ?? ""}
          onChange={handleChange}
          min={setting.constraints?.min}
          max={setting.constraints?.max}
          step={setting.constraints?.step}
        />
      )}
      <div className="setting-input__meta">
        <span className="setting-input__default">Default: {JSON.stringify(setting.default_value)}</span>
      </div>
    </div>
  );
}

// Execution Controls Panel Component
interface ExecutionControlsPanelProps {
  status: any;
  isLoading: boolean;
  onPause: () => void;
  onResume: () => void;
  isPausing: boolean;
  isResuming: boolean;
}

function ExecutionControlsPanel({
  status,
  isLoading,
  onPause,
  onResume,
  isPausing,
  isResuming,
}: ExecutionControlsPanelProps) {
  if (isLoading) {
    return <Skeleton height={150} />;
  }

  // Execution status: "running" = active, "paused" = paused
  // This is simpler and more reliable than Circuit Breaker
  const isPaused = status?.status === "paused";

  // Debug logging
  if (import.meta.env.DEV) {
    logger.debug("ExecutionControlsPanel state:", {
      status: status?.status,
      message: status?.message,
      isPaused,
      isPausing,
      isResuming,
    });
  }

  const handleResumeClick = () => {
    logger.info("Resume button clicked in ExecutionControlsPanel");
    if (isResuming) {
      logger.warn("Resume already in progress, ignoring click");
      return;
    }
    try {
      onResume();
    } catch (error) {
      logger.error("Error in handleResumeClick:", error instanceof Error ? error : new Error(String(error)));
    }
  };

  const handlePauseClick = () => {
    logger.info("Pause button clicked in ExecutionControlsPanel");
    if (isPausing) {
      logger.warn("Pause already in progress, ignoring click");
      return;
    }
    try {
      onPause();
    } catch (error) {
      logger.error("Error in handlePauseClick:", error instanceof Error ? error : new Error(String(error)));
    }
  };

  return (
    <Card className="execution-controls-panel">
      <h2>Execution Controls</h2>
      <div className="execution-controls-panel__content">
        <div className="execution-controls-panel__status">
          <div>
            <strong>Execution Status:</strong> {status?.status || "unknown"}
          </div>
          <div>
            <strong>Message:</strong> {status?.message || "No message"}
          </div>
          <div style={{ marginTop: "12px", fontSize: "0.875rem", color: "var(--color-text-secondary)" }}>
            {isPaused ? (
              <span style={{ color: "var(--color-warning)" }}>
                ⚠️ System execution is paused. No new recommendations will be processed.
              </span>
            ) : (
              <span style={{ color: "var(--color-success)" }}>
                ✅ System execution is running. Recommendations will be processed automatically.
              </span>
            )}
          </div>
        </div>
        <div className="execution-controls-panel__actions">
          {isPaused ? (
            <Button
              variant="primary"
              onClick={handleResumeClick}
              disabled={isResuming}
            >
              {isResuming ? "Resuming..." : "Resume Execution"}
            </Button>
          ) : (
            <Button
              variant="danger"
              onClick={handlePauseClick}
              disabled={isPausing}
            >
              {isPausing ? "Pausing..." : "Pause Execution"}
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

// Audit View Component
function AuditView() {
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const {
    data: auditData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["auditLog", page],
    queryFn: () => getAuditLog(pageSize, (page - 1) * pageSize),
  });

  if (isLoading) {
    return <Skeleton height={400} />;
  }

  if (error) {
    return (
      <Card>
        <h2>Error Loading Audit Log</h2>
        <p>{error instanceof Error ? error.message : "Unknown error"}</p>
      </Card>
    );
  }

  return (
    <Card className="audit-view">
      <h2>Settings Audit Log</h2>
      <div className="audit-view__table">
        <table>
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Admin</th>
              <th>Setting</th>
              <th>Old Value</th>
              <th>New Value</th>
              <th>Section</th>
            </tr>
          </thead>
          <tbody>
            {auditData?.entries.map((entry) => (
              <tr key={entry.id}>
                <td>{new Date(entry.created_at).toLocaleString("en-US")}</td>
                <td>{entry.admin_user_id}</td>
                <td>{entry.setting_key}</td>
                <td>{JSON.stringify(entry.old_value)}</td>
                <td>{JSON.stringify(entry.new_value)}</td>
                <td>{entry.section || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="audit-view__pagination">
        <Button
          variant="secondary"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1}
        >
          Previous
        </Button>
        <span>Page {page}</span>
        <Button
          variant="secondary"
          onClick={() => setPage((p) => p + 1)}
          disabled={!auditData || auditData.entries.length < pageSize}
        >
          Next
        </Button>
      </div>
    </Card>
  );
}

// Readiness Progress Bar Component
interface ReadinessBarProps {
  label: string;
  current: number;
  required: number;
  progress: number;
  met: boolean;
}

function ReadinessBar({ label, current, required, progress, met }: ReadinessBarProps) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <div style={{
        minWidth: "110px",
        fontSize: "0.8rem",
        color: "rgba(203, 213, 225, 0.7)",
        fontWeight: 500,
      }}>
        {label}
      </div>
      <div style={{ flex: 1, height: "20px", borderRadius: "10px", background: "rgba(30, 41, 59, 0.6)", overflow: "hidden" }}>
        <div style={{
          height: "100%",
          width: `${Math.min(100, progress)}%`,
          borderRadius: "10px",
          background: met
            ? "linear-gradient(90deg, rgb(34, 197, 94), rgb(74, 222, 128))"
            : "linear-gradient(90deg, rgb(59, 130, 246), rgb(96, 165, 250))",
          transition: "width 0.5s ease-in-out",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
          <span style={{
            fontSize: "0.7rem",
            fontWeight: 700,
            color: "white",
            textShadow: "0 1px 2px rgba(0,0,0,0.3)",
          }}>
            {Math.round(progress)}%
          </span>
        </div>
      </div>
      <div style={{
        minWidth: "120px",
        fontSize: "0.8rem",
        color: met ? "rgb(34, 197, 94)" : "rgba(203, 213, 225, 0.6)",
        fontWeight: met ? 600 : 400,
        textAlign: "right",
      }}>
        {typeof current === "number" ? current.toFixed(1) : current} / {required}
        {met && " ✓"}
      </div>
    </div>
  );
}

// Reset Confirmation Modal Component
interface ResetConfirmModalProps {
  scope: string;
  section?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isResetting: boolean;
}

function ResetConfirmModal({
  scope,
  section,
  onConfirm,
  onCancel,
  isResetting,
}: ResetConfirmModalProps) {
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>Confirm Reset</h2>
        <p>
          Are you sure you want to reset {scope === "all" ? "all settings" : `settings in section "${section}"`} to
          their default values?
        </p>
        <div className="modal-actions">
          <Button variant="danger" onClick={onConfirm} disabled={isResetting}>
            {isResetting ? "Resetting..." : "Confirm Reset"}
          </Button>
          <Button variant="secondary" onClick={onCancel} disabled={isResetting}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

// Import Modal Component
interface ImportModalProps {
  onClose: () => void;
  preview: ImportPreviewResponse | null;
  onPreview: (request: ImportRequest) => void;
  onImport: (request: ImportRequest) => void;
  isPreviewing: boolean;
  isImporting: boolean;
}

function ImportModal({
  onClose,
  preview,
  onPreview,
  onImport,
  isPreviewing,
  isImporting,
}: ImportModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [importMode, setImportMode] = useState<"merge" | "replace">("merge");
  const [fileError, setFileError] = useState<string | null>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith(".json")) {
      setFileError("Please select a JSON file");
      return;
    }

    setFile(selectedFile);
    setFileError(null);

    // Read file and preview
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target?.result as string);
        if (!json.settings) {
          setFileError("Invalid format: missing 'settings' field");
          return;
        }
        onPreview({ settings: json.settings, mode: importMode });
      } catch (error) {
        setFileError("Invalid JSON file");
      }
    };
    reader.readAsText(selectedFile);
  };

  // Re-preview when mode changes
  useEffect(() => {
    if (file && preview) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const json = JSON.parse(event.target?.result as string);
          if (json.settings) {
            onPreview({ settings: json.settings, mode: importMode });
          }
        } catch (error) {
          // Ignore errors on mode change
        }
      };
      reader.readAsText(file);
    }
  }, [importMode]);

  const handleImport = () => {
    if (!file || !preview) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target?.result as string);
        onImport({ settings: json.settings, mode: importMode });
      } catch (error) {
        setFileError("Invalid JSON file");
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content import-modal" onClick={(e) => e.stopPropagation()}>
        <h2>Import Settings</h2>

        <div className="import-modal__mode">
          <label>
            <input
              type="radio"
              value="merge"
              checked={importMode === "merge"}
              onChange={(e) => setImportMode(e.target.value as "merge" | "replace")}
            />
            Merge (only update existing keys)
          </label>
          <label>
            <input
              type="radio"
              value="replace"
              checked={importMode === "replace"}
              onChange={(e) => setImportMode(e.target.value as "merge" | "replace")}
            />
            Replace (update all, reset missing to defaults)
          </label>
        </div>

        <div className="import-modal__file-select">
          <input
            type="file"
            accept=".json"
            onChange={handleFileSelect}
            disabled={isPreviewing || isImporting}
          />
          {fileError && <p className="error">{fileError}</p>}
        </div>

        {preview && (
          <div className="import-modal__preview">
            <h3>Preview Changes</h3>
            {preview.unknown_keys && preview.unknown_keys.length > 0 && (
              <div className="import-modal__warnings">
                <strong>Unknown keys (ignored):</strong>
                <ul>
                  {preview.unknown_keys.map((key) => (
                    <li key={key}>{key}</li>
                  ))}
                </ul>
              </div>
            )}
            {preview.validation_errors && preview.validation_errors.length > 0 && (
              <div className="import-modal__warnings">
                <strong>Validation errors (skipped):</strong>
                <ul>
                  {preview.validation_errors.map((err) => (
                    <li key={err.key}>
                      {err.label}: {err.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="import-modal__diff">
              <table>
                <thead>
                  <tr>
                    <th>Setting</th>
                    <th>Old Value</th>
                    <th>New Value</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.preview
                    .filter((d) => !d.skipped)
                    .map((diff) => (
                      <tr key={diff.key}>
                        <td>{diff.label}</td>
                        <td>{JSON.stringify(diff.old_value)}</td>
                        <td>{JSON.stringify(diff.new_value)}</td>
                        <td>{diff.action || "update"}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <div className="import-modal__summary">
              <p>
                <strong>Changes:</strong> {preview.changes_count || 0}
                {preview.skipped_count && preview.skipped_count > 0 && (
                  <> | <strong>Skipped:</strong> {preview.skipped_count}</>
                )}
              </p>
            </div>
          </div>
        )}

        <div className="modal-actions">
          {preview && preview.changes_count > 0 && (
            <Button
              variant="primary"
              onClick={handleImport}
              disabled={isImporting || !file}
            >
              {isImporting ? "Importing..." : "Confirm Import"}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose} disabled={isImporting}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

// News Services Control Panel Component (Admin only)
interface NewsServicesControlPanelProps {
  status: NewsServicesStatus | undefined;
  isLoading: boolean;
  onToggle: (service: NewsServiceToggleRequest["service"], enabled: boolean) => void;
  isToggling: boolean;
}

function NewsServicesControlPanel({
  status,
  isLoading,
  onToggle,
  isToggling,
}: NewsServicesControlPanelProps) {
  if (isLoading) {
    return <Skeleton height={400} />;
  }

  if (!status) {
    return (
      <Card>
        <h2>News Services Control</h2>
        <p>Unable to load news services status.</p>
      </Card>
    );
  }

  const services = [
    {
      key: "fetching" as const,
      label: "News Fetching",
      description: "Fetch news articles from providers (CryptoPanic, NewsAPI, RSS)",
      enabled: status.fetchingEnabled,
    },
    {
      key: "filtering" as const,
      label: "News Filtering",
      description: "Filter and deduplicate news articles",
      enabled: status.filteringEnabled,
    },
    {
      key: "classification" as const,
      label: "News Classification",
      description: "Classify news articles by symbol",
      enabled: status.classificationEnabled,
    },
    {
      key: "ai_analysis" as const,
      label: "AI News Analysis",
      description: "AI-powered news analysis using LLM (consumes quota)",
      enabled: status.aiAnalysisEnabled,
      warning: "⚠️ This service consumes AI quota quickly",
    },
  ];

  return (
    <Card className="news-services-control-panel">
      <h2>News Services Control</h2>
      <p className="news-services-control-panel__description">
        Control news services to manage quota consumption. You can disable AI analysis while keeping
        news fetching, filtering, and classification active.
      </p>
      <div className="news-services-control-panel__services">
        {services.map((service) => (
          <div key={service.key} className="news-services-control-panel__service">
            <div className="news-services-control-panel__service-info">
              <div className="news-services-control-panel__service-header">
                <h4>{service.label}</h4>
                <span
                  className={`news-services-control-panel__status-badge ${
                    service.enabled
                      ? "news-services-control-panel__status-enabled"
                      : "news-services-control-panel__status-disabled"
                  }`}
                >
                  {service.enabled ? "Enabled" : "Disabled"}
                </span>
              </div>
              <p className="news-services-control-panel__service-description">
                {service.description}
              </p>
              {service.warning && (
                <p className="news-services-control-panel__service-warning">{service.warning}</p>
              )}
            </div>
            <Button
              type="button"
              variant={service.enabled ? "danger" : "primary"}
              onClick={() => onToggle(service.key, !service.enabled)}
              disabled={isToggling}
            >
              {isToggling ? "Updating..." : service.enabled ? "Disable" : "Enable"}
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
}


